/**
 * Serviço de validação de senhas no frontend
 * Data: 20/07/2025
 * Desenvolvedor: Claude AI
 * 
 * Integra com backend para validação de senhas usando regras configuráveis
 * Neuroagentes - Simplex [www.simplex.tec.br]
 */

import api from './api';

/**
 * Regras de senha retornadas pelo backend
 */
export interface PasswordRules {
  minLength: number;
  maxLength: number;
  requireUppercase: boolean;
  requireLowercase: boolean;
  requireNumbers: boolean;
  requireSpecialChars: boolean;
  specialChars: string;
  minStrengthScore: number;
  blockCommonPasswords?: boolean;
  blockUserInfo?: boolean;
  strengthLevels: Array<{
    level: string;
    minScore: number;
    emoji: string;
    color: string;
  }>;
  examples: {
    weak: string;
    medium: string;
    strong: string;
  };
  tips: string[];
}

/**
 * Resultado da validação de senha
 */
export interface PasswordValidationResult {
  isValid: boolean;
  errors: string[];
  warnings: string[];
  suggestions: string[];
  strengthScore: number;
  strengthLevel: string;
  strengthEmoji: string;
}

/**
 * Contexto do usuário para validação
 */
export interface UserContext {
  name?: string;
  email?: string;
  firstName?: string;
}

/**
 * Serviço de validação de senhas
 */
export class PasswordValidationService {
  private static instance: PasswordValidationService;
  private rulesCache: PasswordRules | null = null;
  private cacheTimestamp = 0;
  private readonly CACHE_TTL = 5 * 60 * 1000; // 5 minutos

  private constructor() {}

  public static getInstance(): PasswordValidationService {
    if (!PasswordValidationService.instance) {
      PasswordValidationService.instance = new PasswordValidationService();
    }
    return PasswordValidationService.instance;
  }

  /**
   * Obtém as regras de senha do servidor (com cache)
   */
  async getPasswordRules(): Promise<PasswordRules> {
    const now = Date.now();
    
    // Verificar cache
    if (this.rulesCache && (now - this.cacheTimestamp) < this.CACHE_TTL) {
      return this.rulesCache;
    }

    try {
      console.log('🔐 Buscando regras de senha do servidor...');
      
      const response = await api.get('/api/auth/password-rules');
      
      if (response.data.success) {
        this.rulesCache = response.data.data;
        this.cacheTimestamp = now;
        
        console.log('✅ Regras de senha carregadas:', this.rulesCache);
        return this.rulesCache!;
      } else {
        throw new Error(response.data.message || 'Erro ao obter regras');
      }
    } catch (error) {
      console.error('❌ Erro ao obter regras de senha:', error);
      
      // Retornar regras padrão em caso de erro
      const defaultRules: PasswordRules = {
        minLength: 8,
        maxLength: 128,
        requireUppercase: true,
        requireLowercase: true,
        requireNumbers: true,
        requireSpecialChars: true,
        specialChars: '!@#$%^&*()_+-=[]{}|;:,.<>?',
        minStrengthScore: 60,
        strengthLevels: [
          { level: "muito-fraca", minScore: 0, emoji: "🚫", color: "#f44336" },
          { level: "fraca", minScore: 30, emoji: "⚠️", color: "#ff9800" },
          { level: "media", minScore: 50, emoji: "🔐", color: "#ffeb3b" },
          { level: "forte", minScore: 70, emoji: "🔒", color: "#8bc34a" },
          { level: "muito-forte", minScore: 90, emoji: "🛡️", color: "#4caf50" }
        ],
        examples: {
          weak: "123456",
          medium: "MinhaSenh@123",
          strong: "M1nh@Senh4$uper$egur@2025!"
        },
        tips: [
          "Use uma combinação de letras maiúsculas e minúsculas",
          "Inclua números e caracteres especiais",
          "Evite informações pessoais como nome ou email",
          "Crie senhas com pelo menos 12 caracteres"
        ]
      };

      this.rulesCache = defaultRules;
      this.cacheTimestamp = now;
      return defaultRules;
    }
  }

  /**
   * Valida uma senha no servidor
   */
  async validatePassword(
    password: string,
    userContext?: UserContext
  ): Promise<PasswordValidationResult> {
    try {
      console.log('🔍 Validando senha no servidor...');
      
      const response = await api.post('/api/auth/validate-password', {
        password,
        userContext: userContext || {}
      });

      if (response.data.success) {
        console.log('✅ Senha validada:', response.data.data);
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Erro na validação');
      }
    } catch (error) {
      console.error('❌ Erro ao validar senha:', error);
      
      // Retornar resultado de erro
      return {
        isValid: false,
        errors: ['Erro ao validar senha no servidor'],
        warnings: [],
        suggestions: ['Tente novamente mais tarde'],
        strengthScore: 0,
        strengthLevel: 'muito-fraca',
        strengthEmoji: '🚫'
      };
    }
  }

  /**
   * Validação local rápida (sem chamar servidor)
   */
  validatePasswordLocally(password: string, rules?: PasswordRules): {
    isValid: boolean;
    errors: string[];
    strengthScore: number;
  } {
    const errors: string[] = [];
    
    // Usar regras do cache ou padrões
    const validationRules = rules || this.rulesCache || {
      minLength: 8,
      maxLength: 128,
      requireUppercase: true,
      requireLowercase: true,
      requireNumbers: true,
      requireSpecialChars: true,
      specialChars: '!@#$%^&*()_+-=[]{}|;:,.<>?'
    };

    // Validação de comprimento
    if (password.length < validationRules.minLength) {
      errors.push(`Mínimo ${validationRules.minLength} caracteres`);
    }

    if (password.length > validationRules.maxLength) {
      errors.push(`Máximo ${validationRules.maxLength} caracteres`);
    }

    // Validação de tipos de caracteres
    if (validationRules.requireUppercase && !/[A-Z]/.test(password)) {
      errors.push('Precisa de letra maiúscula');
    }

    if (validationRules.requireLowercase && !/[a-z]/.test(password)) {
      errors.push('Precisa de letra minúscula');
    }

    if (validationRules.requireNumbers && !/[0-9]/.test(password)) {
      errors.push('Precisa de número');
    }

    if (validationRules.requireSpecialChars) {
      const specialCharsRegex = new RegExp(`[${this.escapeRegex(validationRules.specialChars)}]`);
      if (!specialCharsRegex.test(password)) {
        errors.push('Precisa de caractere especial');
      }
    }

    // Calcular score básico
    let score = 0;
    if (password.length >= validationRules.minLength) score += 20;
    if (/[a-z]/.test(password)) score += 15;
    if (/[A-Z]/.test(password)) score += 15;
    if (/[0-9]/.test(password)) score += 15;
    if (new RegExp(`[${this.escapeRegex(validationRules.specialChars)}]`).test(password)) score += 20;
    if (password.length >= 12) score += 10;
    if (new Set(password).size >= password.length * 0.6) score += 5;

    return {
      isValid: errors.length === 0,
      errors,
      strengthScore: Math.min(100, score)
    };
  }

  /**
   * Obtém a cor para o nível de força
   */
  getStrengthColor(strengthLevel: string): string {
    const colors: Record<string, string> = {
      'muito-fraca': '#f44336',
      'fraca': '#ff9800',
      'media': '#ffeb3b',
      'forte': '#8bc34a',
      'muito-forte': '#4caf50'
    };
    
    return colors[strengthLevel] || '#9e9e9e';
  }

  /**
   * Obtém emoji para o nível de força
   */
  getStrengthEmoji(strengthLevel: string): string {
    const emojis: Record<string, string> = {
      'muito-fraca': '🚫',
      'fraca': '⚠️',
      'media': '🔐',
      'forte': '🔒',
      'muito-forte': '🛡️'
    };
    
    return emojis[strengthLevel] || '🔐';
  }

  /**
   * Limpa o cache de regras
   */
  clearCache(): void {
    this.rulesCache = null;
    this.cacheTimestamp = 0;
    console.log('🗑️ Cache de regras de senha limpo');
  }

  /**
   * Escapa caracteres especiais para regex
   */
  private escapeRegex(string: string): string {
    return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  /**
   * Gera uma mensagem de feedback amigável
   */
  static formatValidationMessage(result: PasswordValidationResult): string {
    if (result.isValid) {
      return `✅ Senha ${result.strengthLevel}! ${result.strengthEmoji}`;
    }

    const mainErrors = result.errors.slice(0, 2);
    let message = `❌ ${mainErrors.join(' • ')}`;
    
    if (result.suggestions.length > 0) {
      const mainSuggestions = result.suggestions.slice(0, 1);
      message += `\n💡 ${mainSuggestions.join(' • ')}`;
    }

    return message;
  }
}

// Exportar instância singleton
export const passwordValidationService = PasswordValidationService.getInstance();